export const HLS_BW_STORAGE_KEY = 'vp-hls-bw'

export type NetworkHint = {
  saveData?: boolean
  effectiveType?: string
  downlink?: number
}

export type HlsPlaybackProfile = {
  estimateBps: number
  conservative: boolean
}

type NavigatorConnection = Navigator & {
  connection?: NetworkHint & EventTarget
  mozConnection?: NetworkHint & EventTarget
  webkitConnection?: NetworkHint & EventTarget
}

function connectionFrom(source: Navigator | undefined) {
  if (!source) return undefined
  const nav = source as NavigatorConnection
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection
}

export function readNetworkHint(
  source: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator,
): NetworkHint | undefined {
  return connectionFrom(source)
}

export function readNetworkTarget(
  source: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator,
): EventTarget | undefined {
  return connectionFrom(source)
}

export function readStoredBandwidth(storage: Storage | undefined): number | undefined {
  if (!storage) return undefined
  try {
    const raw = Number(storage.getItem(HLS_BW_STORAGE_KEY))
    return Number.isFinite(raw) && raw > 0 ? raw : undefined
  } catch {
    return undefined
  }
}

export function persistBandwidthEstimate(storage: Storage | undefined, bitsPerSecond: number) {
  if (!storage || !Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return
  try {
    storage.setItem(HLS_BW_STORAGE_KEY, String(Math.round(bitsPerSecond)))
  } catch {
    /* private mode */
  }
}

export function hlsPlaybackProfile(input: {
  stored?: number
  network?: NetworkHint
}): HlsPlaybackProfile {
  const type = input.network?.effectiveType
  const conservative = Boolean(
    input.network?.saveData || type === 'slow-2g' || type === '2g' || type === '3g',
  )
  if (input.network?.saveData || type === 'slow-2g' || type === '2g') {
    return { estimateBps: 250_000, conservative: true }
  }
  if (type === '3g') {
    return { estimateBps: 400_000, conservative: true }
  }
  if (typeof input.stored === 'number' && Number.isFinite(input.stored) && input.stored > 0) {
    return { estimateBps: Math.min(Math.max(input.stored, 250_000), 8_000_000), conservative }
  }
  if (typeof input.network?.downlink === 'number' && input.network.downlink > 0) {
    return {
      estimateBps: Math.round(input.network.downlink * 1_000_000 * 0.7),
      conservative,
    }
  }
  return { estimateBps: 500_000, conservative }
}

export function hlsPlaybackConfig(
  xhrSetup: (xhr: XMLHttpRequest, url: string) => void,
  profile: HlsPlaybackProfile,
) {
  return {
    xhrSetup,
    capLevelToPlayerSize: true,
    ignoreDevicePixelRatio: true,
    startLevel: -1,
    testBandwidth: false,
    startFragPrefetch: true,
    abrEwmaDefaultEstimate: profile.estimateBps,
    abrEwmaDefaultEstimateMax: profile.conservative ? 1_200_000 : 5_000_000,
    abrBandWidthFactor: 0.85,
    abrBandWidthUpFactor: 0.55,
    maxBufferLength: 30,
    maxMaxBufferLength: 90,
    backBufferLength: 30,
    maxStarvationDelay: 4,
    progressive: true,
  }
}

export function autoLevelCapIndex(
  levels: readonly { height?: number }[],
  conservative: boolean,
): number {
  if (!conservative) return -1
  let cap = -1
  for (let index = 0; index < levels.length; index += 1) {
    if ((levels[index]?.height ?? 0) <= 360) cap = index
  }
  return cap
}

export type PlaybackLoaderContext = {
  url: string
  type?: string
  frag?: { start?: number }
}

type LoaderCallbacks = {
  onError: (error: { code: number; text: string }, context: PlaybackLoaderContext, networkDetails: unknown, stats: unknown) => void
}

type LoaderInstance = {
  load: (context: PlaybackLoaderContext, config: unknown, callbacks: LoaderCallbacks) => void
}

type LoaderCtor = new (config: unknown) => LoaderInstance

export function playlistUrlWithStart(url: string, startSeconds: number): string {
  if (/master\.m3u8/i.test(url) || !/\.m3u8/i.test(url)) return url
  try {
    const parsed = new URL(url, 'http://local.invalid')
    parsed.searchParams.set('start', String(Math.max(0, Math.floor(startSeconds))))
    if (/^https?:\/\//i.test(url)) return parsed.toString()
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}

export function isSignedMediaUrl(url: string): boolean {
  return /[?&](X-Amz-Signature|X-Goog-Signature|X-Goog-Algorithm|signed=)/i.test(url)
}

export function sameOriginMediaUrl(levelUrl: string, segmentUrl: string): string | undefined {
  const path = segmentUrl.split('?')[0] ?? ''
  const name = path.split('/').pop()
  if (!name || !/\.ts$/i.test(name)) return undefined
  const fromObject = path.match(/\/hls\/(\d+p)\/segment_\d+\.ts$/i)?.[1]
  try {
    const parsed = new URL(levelUrl || segmentUrl, 'http://local.invalid')
    const match = parsed.pathname.match(/^(.*)\/(master|\d+p)\.m3u8$/i)
    const prefix = match?.[1]
    const label = match?.[2]
    if (prefix === undefined || !label) return undefined
    const rendition = fromObject ?? (label.toLowerCase() === 'master' ? '360p' : label)
    const nextPath = `${prefix}/${rendition}/${name}`
    if (/^https?:\/\//i.test(levelUrl)) {
      parsed.pathname = nextPath
      parsed.search = ''
      return parsed.toString()
    }
    return nextPath
  } catch {
    return undefined
  }
}

export function signedUrlForSegment(playlist: string, segmentUrl: string): string | undefined {
  const name = segmentUrl.split('?')[0]?.split('/').pop()
  if (!name) return undefined
  for (const line of playlist.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.includes(name) && /^https?:\/\//i.test(trimmed)) return trimmed
  }
  return undefined
}

let lastLevelUrl = ''

export function createPlaybackWindowLoader(
  Base: LoaderCtor,
  getPosition: () => number,
  _xhrSetup?: (xhr: XMLHttpRequest, url: string) => void,
): LoaderCtor {
  return class PlaybackWindowLoader extends Base {
    override load = (context: PlaybackLoaderContext, config: unknown, callbacks: LoaderCallbacks) => {
      if (context.type === 'manifest') {
        super.load(context, config, callbacks)
        return
      }
      if (context.type === 'level' || context.type === 'audioTrack') {
        context.url = playlistUrlWithStart(context.url, getPosition())
        lastLevelUrl = context.url
        super.load(context, config, callbacks)
        return
      }
      if (isSignedMediaUrl(context.url)) {
        const proxied = sameOriginMediaUrl(lastLevelUrl, context.url)
        if (proxied) context.url = proxied
        super.load(context, config, callbacks)
        return
      }
      super.load(context, config, callbacks)
    }
  }
}
